import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, Chip, Grid, Card, CardContent,
  LinearProgress, Alert, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControlLabel, Switch, IconButton, Tooltip, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, Avatar, Tabs, Tab,
} from '@mui/material';
import { BeachAccess, Add, Edit, Delete, Close, CalendarMonth } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000';
function apiFetch(path: string, opts: RequestInit = {}) {
  return fetch(`${API}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}`, ...(opts.headers || {}) },
  }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.message || 'Error'); return d; });
}

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];

interface Holiday { id: string; name: string; date: string; year: number; countryId: number; isRecurring: boolean; description?: string; }

function daysUntil(dateStr: string): number {
  const today = new Date(); today.setHours(0,0,0,0);
  const d = new Date(dateStr + 'T00:00:00');
  return Math.round((d.getTime() - today.getTime()) / 86400000);
}

const emptyForm = (): Partial<Holiday> => ({
  name: '', date: '', isRecurring: false, description: '',
  year: new Date().getFullYear(), countryId: 852,
});

export default function PublicHolidays() {
  const { user } = useAuth();
  const isHR = ['hr_admin', 'hr_manager'].includes(user?.role ?? '');

  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [tab, setTab] = useState(0);
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formDialog, setFormDialog] = useState<Partial<Holiday> | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [data, st] = await Promise.all([
        apiFetch(`/api/public-holidays?year=${year}`),
        apiFetch(`/api/public-holidays/stats?year=${year}`),
      ]);
      setHolidays(Array.isArray(data) ? data : []);
      setStats(st);
    } catch (e: any) { setError(e.message); }
    setLoading(false);
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!formDialog) return;
    try {
      formDialog.id
        ? await apiFetch(`/api/public-holidays/${formDialog.id}`, { method: 'PATCH', body: JSON.stringify(formDialog) })
        : await apiFetch('/api/public-holidays', { method: 'POST', body: JSON.stringify(formDialog) });
      setFormDialog(null); load();
    } catch (e: any) { setError(e.message); }
  };

  const del = async () => {
    if (!deleteId) return;
    try { await apiFetch(`/api/public-holidays/${deleteId}`, { method: 'DELETE' }); setDeleteId(null); load(); }
    catch (e: any) { setError(e.message); }
  };

  // Group by month
  const byMonth: Record<number, Holiday[]> = {};
  for (const h of holidays) {
    const m = parseInt(h.date.slice(5, 7));
    byMonth[m] = [...(byMonth[m] ?? []), h];
  }

  const todayStr = new Date().toISOString().slice(0, 10);
  const upcoming = holidays.filter(h => h.date >= todayStr).slice(0, 5);

  // Calendar view helpers
  const today = new Date();

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BeachAccess sx={{ fontSize: 32, color: '#e65100' }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>Public Holidays</Typography>
            <Typography variant="body2" color="text.secondary">HK statutory holidays · countryId 852</Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button variant="outlined" size="small" onClick={() => setYear(y => y - 1)}>‹ {year - 1}</Button>
          <Chip label={year} color="primary" />
          <Button variant="outlined" size="small" onClick={() => setYear(y => y + 1)}>{year + 1} ›</Button>
          {isHR && (
            <Button variant="contained" startIcon={<Add />} sx={{ ml: 1, bgcolor: '#e65100' }}
              onClick={() => setFormDialog(emptyForm())}>
              Add Holiday
            </Button>
          )}
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Stats */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Total Holidays', value: stats.total, color: '#e65100' },
            { label: 'Upcoming', value: stats.upcoming, color: '#1a237e' },
            { label: 'Next Holiday', value: stats.nextHoliday?.name ?? 'None', small: true, color: '#2e7d32' },
            { label: 'Next Date', value: stats.nextHoliday?.date ? new Date(stats.nextHoliday.date + 'T00:00:00').toLocaleDateString('en-HK', { day: 'numeric', month: 'long' }) : '—', small: true, color: '#37474f' },
          ].map(s => (
            <Grid key={s.label} size={{ xs: 6, sm: 3 }}>
              <Card sx={{ textAlign: 'center', p: 1 }}>
                <CardContent sx={{ pb: '8px !important' }}>
                  <Typography variant={s.small ? 'h6' : 'h4'} fontWeight={700} color={s.color} noWrap>{s.value}</Typography>
                  <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Next upcoming banner */}
      {upcoming.length > 0 && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: '#fff8e1', border: '1px solid #ffcc02' }}>
          <Typography fontWeight={700} sx={{ mb: 1 }}>🗓️ Coming Up</Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {upcoming.map(h => {
              const diff = daysUntil(h.date);
              return (
                <Chip key={h.id}
                  label={`${h.name} · ${diff === 0 ? 'Today!' : diff === 1 ? 'Tomorrow' : `${diff}d`}`}
                  size="small"
                  color={diff <= 7 ? 'warning' : 'default'}
                  variant={diff <= 7 ? 'filled' : 'outlined'}
                />
              );
            })}
          </Box>
        </Paper>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="By Month" />
        <Tab label="Full List" />
        {stats && <Tab label={`Month Breakdown`} />}
      </Tabs>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* ── BY MONTH VIEW ── */}
      {tab === 0 && (
        <Grid container spacing={2}>
          {MONTHS.map((mon, i) => {
            const m = i + 1;
            const mHols = byMonth[m] ?? [];
            const isPast = year < currentYear || (year === currentYear && m < today.getMonth() + 1);
            return (
              <Grid key={m} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                <Paper variant="outlined" sx={{ p: 1.5, opacity: isPast ? 0.6 : 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography fontWeight={700} variant="body2">{MONTH_NAMES[i]}</Typography>
                    {mHols.length > 0 && <Chip label={mHols.length} size="small" color="warning" />}
                  </Box>
                  {mHols.length === 0 ? (
                    <Typography variant="caption" color="text.secondary">No holidays</Typography>
                  ) : (
                    mHols.map(h => (
                      <Box key={h.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Avatar sx={{ width: 24, height: 24, bgcolor: '#fff3e0', color: '#e65100', fontSize: '0.65rem', fontWeight: 700 }}>
                          {new Date(h.date + 'T00:00:00').getDate()}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="caption" fontWeight={600} noWrap>{h.name}</Typography>
                        </Box>
                        {h.isRecurring && <Chip label="🔁" size="small" sx={{ fontSize: '0.55rem', height: 16 }} />}
                        {isHR && (
                          <Box sx={{ display: 'flex' }}>
                            <IconButton size="small" sx={{ p: 0.3 }} onClick={() => setFormDialog({ ...h })}><Edit sx={{ fontSize: 12 }} /></IconButton>
                            <IconButton size="small" sx={{ p: 0.3 }} color="error" onClick={() => setDeleteId(h.id)}><Delete sx={{ fontSize: 12 }} /></IconButton>
                          </Box>
                        )}
                      </Box>
                    ))
                  )}
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* ── FULL LIST ── */}
      {tab === 1 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell><b>Date</b></TableCell>
                <TableCell><b>Day</b></TableCell>
                <TableCell><b>Holiday</b></TableCell>
                <TableCell><b>Days Away</b></TableCell>
                <TableCell><b>Recurring</b></TableCell>
                {isHR && <TableCell><b>Actions</b></TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {holidays.map(h => {
                const d = new Date(h.date + 'T00:00:00');
                const diff = daysUntil(h.date);
                const isPast = diff < 0;
                return (
                  <TableRow key={h.id} hover sx={{ opacity: isPast ? 0.55 : 1 }}>
                    <TableCell sx={{ fontWeight: 700 }}>{d.toLocaleDateString('en-HK', { day: 'numeric', month: 'short', year: 'numeric' })}</TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>{d.toLocaleDateString('en-HK', { weekday: 'long' })}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{h.name}</Typography>
                      {h.description && <Typography variant="caption" color="text.secondary">{h.description}</Typography>}
                    </TableCell>
                    <TableCell>
                      {isPast ? <Chip label="Past" size="small" sx={{ opacity: 0.5 }} /> :
                        diff === 0 ? <Chip label="Today 🎉" size="small" color="success" /> :
                        diff <= 14 ? <Chip label={`${diff}d`} size="small" color="warning" /> :
                        <Typography variant="caption" color="text.secondary">{diff}d</Typography>
                      }
                    </TableCell>
                    <TableCell>{h.isRecurring ? <Chip label="Annual 🔁" size="small" color="info" /> : <Typography variant="caption" color="text.secondary">One-off</Typography>}</TableCell>
                    {isHR && (
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title="Edit"><IconButton size="small" onClick={() => setFormDialog({ ...h })}><Edit fontSize="small" /></IconButton></Tooltip>
                          <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setDeleteId(h.id)}><Delete fontSize="small" /></IconButton></Tooltip>
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
              {holidays.length === 0 && !loading && (
                <TableRow><TableCell colSpan={isHR ? 6 : 5} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>No holidays for {year}.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ── MONTH CHART ── */}
      {tab === 2 && stats && (
        <Paper sx={{ p: 3 }}>
          <Typography fontWeight={700} sx={{ mb: 2 }}>Holidays Per Month — {year}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, height: 160 }}>
            {stats.byMonth.map((m: any) => {
              const h = m.count * 60;
              return (
                <Box key={m.month} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                  {m.count > 0 && (
                    <Box sx={{ width: '100%', height: h, bgcolor: '#e65100', borderRadius: '4px 4px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography variant="caption" color="white" fontWeight={700}>{m.count}</Typography>
                    </Box>
                  )}
                  {m.count === 0 && <Box sx={{ flex: 1 }} />}
                  <Typography variant="caption" color="text.secondary">{MONTHS[m.month - 1]}</Typography>
                </Box>
              );
            })}
          </Box>
        </Paper>
      )}

      {/* ── FORM DIALOG ── */}
      <Dialog open={!!formDialog} onClose={() => setFormDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {formDialog?.id ? 'Edit Holiday' : 'Add Public Holiday'}
          <IconButton sx={{ position: 'absolute', right: 8, top: 8 }} onClick={() => setFormDialog(null)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField label="Holiday Name *" fullWidth value={formDialog?.name ?? ''}
              onChange={e => setFormDialog(p => ({ ...p, name: e.target.value }))} />
            <TextField label="Date *" type="date" fullWidth InputLabelProps={{ shrink: true }}
              value={formDialog?.date?.slice(0, 10) ?? ''}
              onChange={e => {
                const d = e.target.value;
                setFormDialog(p => ({ ...p, date: d, year: d ? parseInt(d.slice(0, 4)) : p?.year }));
              }} />
            <TextField label="Description" fullWidth value={formDialog?.description ?? ''}
              onChange={e => setFormDialog(p => ({ ...p, description: e.target.value }))} />
            <FormControlLabel
              control={<Switch checked={formDialog?.isRecurring ?? false}
                onChange={e => setFormDialog(p => ({ ...p, isRecurring: e.target.checked }))} />}
              label="Recurring annually" />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormDialog(null)}>Cancel</Button>
          <Button variant="contained" sx={{ bgcolor: '#e65100' }} onClick={save}
            disabled={!formDialog?.name || !formDialog?.date}>
            {formDialog?.id ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── DELETE CONFIRM ── */}
      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Holiday?</DialogTitle>
        <DialogContent>
          <Typography>This will permanently remove this holiday from the calendar.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={del}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
